/**
 * "Ask AI" inside a form (068): a side drawer that answers questions about
 * the form's submissions, helps troubleshoot it, and proposes edits.
 *
 * The drawer is a conversation the server does not keep — it lives here, for
 * as long as the form is open, and each question sends it along with the
 * working copy (unsaved edits included). A proposal is shown as a list of
 * sentences; "Apply to draft" makes the edits through `applyProposal` and
 * commits them as ONE undo step, so Undo takes the lot back and Publish is
 * still the only thing that reaches respondents.
 *
 * Nothing renders unless the platform has a key. A plan without the feature
 * still sees the button — its first question answers with the upsell, the
 * way every other gated path does.
 */
import { FormEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AssistantInfo, AssistantProposal, askAssistantApi, assistantInfoApi,
} from '../../api/forms';
import { PlanLimitBody, planDisplayName, planLimitOf } from '../../api/limits';
import PlanLimitBlock, { PLANS_PATH } from '../../components/PlanLimitBlock';
import { useToast } from '../../components/Toast';
import type { FormDraft } from './FormWorkspace';
import { ApplyResult, applyProposal, describeChange, formSnapshot } from './assistantModel';

interface Turn {
  role: 'user' | 'assistant';
  text: string;
  proposal?: AssistantProposal | null;
  /** what happened to the proposal: applied (with what was skipped) or dismissed */
  outcome?: { applied: number; skipped: string[] } | 'dismissed';
}

const MAX_CHARS = 4000;

export default function FormAssistant({ formId, draft, onApply }: {
  formId: number;
  /** the working copy; null while the builder payload loads */
  draft: FormDraft | null;
  /** commit a whole new working copy as one undoable change */
  onApply: (next: FormDraft) => void;
}) {
  const [info, setInfo] = useState<AssistantInfo | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    assistantInfoApi(formId)
      .then((r) => { if (live) setInfo({ available: r.available, submissionAccess: r.submissionAccess, canEdit: r.canEdit }); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [formId]);

  if (!info?.available) return null;
  return (
    <>
      <button className="fs-btn neutral sm" onClick={() => setOpen(true)} aria-haspopup="dialog">
        ✨ Ask AI
      </button>
      {/* Kept mounted while closed, so the conversation survives a close. */}
      <AssistantPanel
        open={open} onClose={() => setOpen(false)} formId={formId} info={info} draft={draft} onApply={onApply}
      />
    </>
  );
}

function AssistantPanel({ open, onClose, formId, info, draft, onApply }: {
  open: boolean;
  onClose: () => void;
  formId: number;
  info: AssistantInfo;
  draft: FormDraft | null;
  onApply: (next: FormDraft) => void;
}) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState<PlanLimitBody | null>(null);
  const toast = useToast();
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    inputRef.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // The newest message in view.
  useEffect(() => {
    const el = bodyRef.current;
    if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight });
  }, [turns, busy]);

  const send = useCallback(async (text: string) => {
    const q = text.trim();
    if (!q || busy || !draft) return;
    const next: Turn[] = [...turns, { role: 'user', text: q }];
    setTurns(next);
    setInput('');
    setError('');
    setLimit(null);
    setBusy(true);
    try {
      const r = await askAssistantApi(
        formId, next.map((t) => ({ role: t.role, text: t.text })), formSnapshot(draft),
      );
      setTurns([...next, { role: 'assistant', text: r.text, proposal: r.proposal }]);
    } catch (e: unknown) {
      const lim = planLimitOf(e);
      if (lim) setLimit(lim);
      else setError((e as Error).message || 'The assistant could not answer.');
      // The question goes back in the box, so a reword is one edit away.
      setTurns(turns);
      setInput(q);
    } finally {
      setBusy(false);
    }
  }, [busy, draft, formId, turns]);

  const apply = (i: number) => {
    const t = turns[i];
    if (!t.proposal || !draft) return;
    const r: ApplyResult = applyProposal(draft, t.proposal.changes);
    if (r.applied) {
      onApply(r.draft);
      toast(`Applied to the draft — respondents see nothing until you publish.`);
    }
    setTurns((ts) => ts.map((x, j) => (j === i ? { ...x, outcome: { applied: r.applied, skipped: r.skipped } } : x)));
  };
  const dismiss = (i: number) =>
    setTurns((ts) => ts.map((x, j) => (j === i ? { ...x, outcome: 'dismissed' } : x)));

  const onSubmit = (e: FormEvent) => { e.preventDefault(); void send(input); };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(input); }
  };

  if (!open) return null;

  const starters = [
    info.submissionAccess ? 'How many submissions came in over the last 7 days?' : null,
    info.submissionAccess ? 'Summarise what people answered this month.' : null,
    'Check this form for problems — rules that never fire, hidden questions, confusing wording.',
    info.canEdit ? 'Add a phone number field after the email address.' : null,
  ].filter((s): s is string => !!s);

  return (
    <>
      <div className="fs-scrim" onClick={onClose} />
      <div className="fs-drawer fa-panel" role="dialog" aria-modal="true" aria-label="Ask AI about this form">
        <div className="fs-drawer-head fa-head">
          <div className="fa-title">
            <h3>Ask AI about this form</h3>
            {turns.length > 0 && (
              <button className="fs-btn neutral sm" disabled={busy} onClick={() => { setTurns([]); setError(''); setLimit(null); }}>
                New conversation
              </button>
            )}
            <button className="fs-btn icon" title="Close" aria-label="Close" onClick={onClose}>✕</button>
          </div>
          <div className="fa-notes">
            {!info.submissionAccess && (
              <p className="fs-cell-sub">
                It cannot read this account&rsquo;s submissions. An Owner or Admin can allow that
                in <Link to="/admin/settings/privacy">Settings → Privacy</Link>.
              </p>
            )}
            {!info.canEdit && (
              <p className="fs-cell-sub">You can view this form, so it can explain changes but not propose them.</p>
            )}
          </div>
        </div>

        <div className="fs-drawer-body fa-body" ref={bodyRef} aria-live="polite">
          {turns.length === 0 && (
            <div className="fa-empty">
              <p className="fs-cell-sub">
                Ask about the answers people gave, why the form behaves the way it does, or what to
                change. Edits are proposed for you to apply to the draft — nothing goes live until you publish.
              </p>
              <div className="fa-starters">
                {starters.map((s) => (
                  <button key={s} className="fa-starter" disabled={!draft} onClick={() => { void send(s); }}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {turns.map((t, i) => (
            <div key={i} className={`fa-msg ${t.role}`}>
              <div className="fa-bubble">{t.text}</div>
              {t.proposal && draft && (
                <ProposalCard
                  proposal={t.proposal} draft={draft} canEdit={info.canEdit} outcome={t.outcome}
                  onApply={() => apply(i)} onDismiss={() => dismiss(i)}
                />
              )}
            </div>
          ))}
          {busy && <div className="fa-msg assistant"><div className="fa-bubble fa-thinking" role="status">Thinking…</div></div>}
          {error && <div className="ad-err">{error}</div>}
          {limit && (limit.limit === 'aiBuilder'
            ? (
              <div className="lim-block">
                <div><span className="lim-pill">Plan limit</span></div>
                <div>
                  <b>The AI assistant isn&rsquo;t included on {planDisplayName(limit.plan)}.</b>{' '}
                  {limit.upgradeTo ? `${planDisplayName(limit.upgradeTo)} includes it. ` : ''}
                  <Link className="lim-link" to={PLANS_PATH}>See plans</Link>
                </div>
              </div>
            )
            : <PlanLimitBlock body={limit} />)}
        </div>

        <form className="fs-drawer-foot fa-foot" onSubmit={onSubmit}>
          <textarea
            ref={inputRef} className="fs-input fa-input" rows={2} value={input} maxLength={MAX_CHARS}
            placeholder="Ask a question or describe a change…" aria-label="Your question"
            onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
          />
          <button className="fs-btn primary" type="submit" disabled={busy || !input.trim() || !draft}>Send</button>
        </form>
      </div>
    </>
  );
}

function ProposalCard({ proposal, draft, canEdit, outcome, onApply, onDismiss }: {
  proposal: AssistantProposal;
  draft: FormDraft;
  canEdit: boolean;
  outcome: Turn['outcome'];
  onApply: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="fa-proposal">
      <div className="fa-proposal-head">
        <span className="fs-pill info">Proposed changes</span>
        <span className="fa-proposal-sum">{proposal.summary}</span>
      </div>
      <ol className="fa-changes">
        {proposal.changes.map((c, i) => <li key={i}>{describeChange(c, draft, proposal.changes)}</li>)}
      </ol>
      {outcome === 'dismissed' && <p className="fs-cell-sub">Dismissed — nothing was changed.</p>}
      {outcome && outcome !== 'dismissed' && (
        <div className="fs-cell-sub">
          {outcome.applied
            ? <>Applied {outcome.applied} change{outcome.applied === 1 ? '' : 's'} to the draft. Undo takes {outcome.applied === 1 ? 'it' : 'them all'} back; Publish makes {outcome.applied === 1 ? 'it' : 'them'} live.</>
            : 'None of these could be applied to the draft as it stands now.'}
          {outcome.skipped.length > 0 && (
            <ul className="fa-skipped">{outcome.skipped.map((s) => <li key={s}>{s}</li>)}</ul>
          )}
        </div>
      )}
      {!outcome && canEdit && (
        <div className="fa-proposal-actions">
          <button className="fs-btn primary sm" onClick={onApply}>Apply to draft</button>
          <button className="fs-btn neutral sm" onClick={onDismiss}>Dismiss</button>
        </div>
      )}
    </div>
  );
}
